/**
 * Unified Model Types
 *
 * Normalizes chat models (from shared-pricing-config.ts) and
 * generation models (from shared-generation-pricing-config.ts)
 * into a single unified type for UI rendering.
 */

import type { ModelCapability } from '../../../../../shared-pricing-config'
import type { PriceUnit } from '../../../../../shared-generation-pricing-config'

export type UnifiedModelType = 'chat' | 'generation'

export interface UptimeHistorySegment {
  start: number
  successCount: number
  serverErrorCount: number
  excludedClientErrorCount: number
}

// Simplified generation type for UI (derived from GenerationType in shared-generation-pricing-config)
export type SimpleGenerationType = 'image' | 'video' | 'audio'

/**
 * Chat model pricing (token-based)
 */
export interface ChatModelPricing {
  type: 'chat'
  inputPricePerMillion: number
  outputPricePerMillion: number
  cacheReadPricePerMillion: number
  // 5-minute cache write rate (this is what cacheCreationPricePerMillion has always meant).
  cacheCreationPricePerMillion: number
  // 1-hour cache write rate (Anthropic only). Optional; when absent, consumers derive
  // it as inputPricePerMillion * 2 — matching the biller's CostCalculator default.
  cacheCreation1hPricePerMillion?: number
}

/**
 * Generation model pricing (per-unit)
 */
export interface GenerationModelPricing {
  type: 'generation'
  generationType: SimpleGenerationType // 'image' | 'video' | 'audio'
  priceMillidollars: number // Price per generation in millidollars (resolved min price from config)
  priceUnit: PriceUnit // What unit the price applies to
}

/**
 * Chat model metadata
 */
export interface ChatModelMetadata {
  maxContext: string
  maxOutput: string
  maxContextTokens: number
  latency?: number
  releasedAt?: string
  capabilities: Array<ModelCapability>
}

/**
 * Generation model metadata
 */
export interface GenerationModelMetadata {
  generationType: SimpleGenerationType
  supportedParams?: Array<string>
  maxDuration?: number
  minDuration?: number
}

/**
 * Unified model representation for UI
 */
export interface UnifiedModel {
  // Common fields
  id: string
  name: string
  provider: string
  family: string
  type: UnifiedModelType

  // Common metadata
  description: string
  strongestFor?: string
  featured?: boolean
  icon?: string
  version?: string
  dataCutoff?: string

  // Discriminated pricing
  pricing: ChatModelPricing | GenerationModelPricing

  // Discriminated metadata
  metadata: ChatModelMetadata | GenerationModelMetadata

  // Tier availability (from backend)
  availableTiers?: Array<string>
  hasDiscount?: boolean
  discountPercent?: number
  /** Observed 30-minute availability segments over the rolling last 24 hours. */
  uptimeHistory?: Array<UptimeHistorySegment>
}

/**
 * Type guards
 */
export function isChatModel(model: UnifiedModel): model is UnifiedModel & {
  pricing: ChatModelPricing
  metadata: ChatModelMetadata
} {
  return model.type === 'chat'
}

export function isGenerationModel(
  model: UnifiedModel,
): model is UnifiedModel & {
  pricing: GenerationModelPricing
  metadata: GenerationModelMetadata
} {
  return model.type === 'generation'
}

/**
 * The `/models/$type/$` URL type segment for a given model — `chat` for
 * chat models, else the generation category (`image` | `video` | `audio`).
 */
export function getModelUrlType(
  model: UnifiedModel,
): 'chat' | SimpleGenerationType {
  if (isGenerationModel(model)) return model.pricing.generationType
  return 'chat'
}
